import { motion } from "framer-motion";
import { Linkedin, Mic } from "lucide-react";

const FOUNDER_IMG = "/faisal-ukcw.jpg";

const STATS = [
  { v: "£63M", l: "Generated in new business" },
  { v: "80+", l: "AI audits this year" },
  { v: "UKCW", l: "Featured AI speaker" },
];

export default function Founder() {
  return (
    <section id="founder" data-testid="founder-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        <div className="flex flex-col gap-4 mb-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#00E5FF]">/ Founder</div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter max-w-3xl">
            Built and signed by one operator. Not a content factory.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#050505] md:col-span-2 relative min-h-[460px] overflow-hidden"
          >
            <img
              src={FOUNDER_IMG}
              alt="Faisal Amin, Founder"
              className="absolute inset-0 h-full w-full object-cover object-[50%_25%]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <div className="font-heading text-xl font-semibold">Faisal Amin</div>
                <div className="text-sm text-white/65 font-mono uppercase tracking-wider">
                  Founder · AI Commercial Growth Partner
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  data-testid="founder-linkedin"
                  href="https://www.linkedin.com/in/faisalamin13/"
                  target="_blank"
                  rel="noreferrer"
                  className="h-9 w-9 grid place-items-center border border-white/25 text-white/80 hover:text-[#00E5FF] hover:border-[#00E5FF] transition-colors bg-black/40 backdrop-blur"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              </div>
            </div>
          </motion.div>

          <div className="bg-[#050505] md:col-span-3 p-8 md:p-12 flex flex-col">
            <div className="font-mono text-xs uppercase tracking-widest text-white/50">/ The take</div>
            <p className="font-heading text-2xl md:text-3xl font-medium mt-5 leading-snug tracking-tight">
              &ldquo;Most firms aren&apos;t failing. They&apos;re{" "}
              <span className="text-[#00E5FF]">growing, but flying blind.</span>{" "}
              No sales engine. No view on where revenue lands in six months. The audit fixes that in a single focused engagement.&rdquo;
            </p>

            <div className="mt-7 space-y-4 text-white/70 leading-relaxed text-[15px]">
              <p>
                I founded a business development consultancy and hit a wall most founders know well, I became my own bottleneck.
                Instead of hiring, I built. I installed three AI-powered outbound systems inside my own business: one to find,
                qualify and book meetings on autopilot. One to chase and nurture prospects. One to reactivate dormant clients.
                The heavy lifting was handled. I focused on closing.
              </p>
              <p>
                That worked so well I integrated the same systems into our BD service for clients, at no extra cost. Then I
                started auditing other businesses to do the same.
              </p>
              <p>
                To date I&apos;ve carried out <span className="text-white">80+ AI audits</span> across{" "}
                <span className="text-white">construction, property and professional services</span>, working hands on with{" "}
                <span className="text-white">30+ businesses</span> to build and integrate AI into how they operate.
              </p>
              <p>
                The pattern is always the same: they know AI is coming, they want to use it, but there&apos;s no clear path,
                no time, and no one to show them how to install it without disrupting what&apos;s already working.
              </p>
              <p className="text-white/85">
                In one focused engagement I show you exactly where AI can generate immediate impact in your sales and outreach,
                without adding headcount, without anything breaking. You leave with a
                <span className="text-[#00E5FF]"> clear, step-by-step report</span> tailored to your business.
              </p>
              <p className="text-white/55 text-sm flex items-start gap-2">
                <Mic className="h-4 w-4 mt-0.5 text-[#00E5FF] shrink-0" />
                Featured AI speaker at UK Construction Week.
              </p>
            </div>

            <div className="mt-auto pt-10 grid grid-cols-3 gap-px bg-white/10 border-t border-white/10">
              {STATS.map((s) => (
                <div key={s.l} className="bg-[#050505] py-5 px-2 md:px-4">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-white">{s.v}</div>
                  <div className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/45 mt-2 leading-snug">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
